import { useEffect, useState, type KeyboardEvent, type WheelEvent } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  Grid,
  Divider,
  Checkbox,
  Select,
  Textarea,
  Card,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAuthStore } from '@/store/auth'
import { SearchableSelect } from '@/components/common/SearchableSelect'
import { Edit, Plus, Trash2 } from 'lucide-react'
import {
  createWaybillDetailSchema,
  updateWaybillDetailSchema,
  type CreateWaybillDetailFormData,
  type UpdateWaybillDetailFormData,
} from '@/lib/validations/auth'

interface DropdownData {
  id: number
  full_name: string
}

interface DriverDropdownData extends DropdownData {
  helper_id?: number | null
  assigned_truck_plate_numbers?: string[]
}

interface ExpenseDropdownData {
  id: number
  total_expenses: string
  expenses: number
  stack_run: number
  docs_fee: number
  online_booking_fee: number
}

interface RatePerClientDropdownData {
  id: number
  rate: string
  stack_run: number
  no_of_days: number
  tax_percent: number | null
  has_vat: boolean
  requirements: string | null
  remarks: string | null
}

interface TruckDropdownData {
  id: number
  plate_number: string
}

interface ContainerData {
  id: number
  container_number: string
}

interface WaybillFormProps {
  waybillId?: number
  bookingId?: number
  bookingReferenceNumber?: string
  shippingLineId?: number
  shippingLineName?: string
  cypaFromName?: string
  cypaToName?: string
  cypaToId?: number
  cypaFromId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

type WaybillFormValues = CreateWaybillDetailFormData | UpdateWaybillDetailFormData

export function WaybillForm({ waybillId, bookingId, bookingReferenceNumber, shippingLineId, shippingLineName, cypaFromName, cypaToName, cypaToId, cypaFromId, onSuccess, onCancel }: WaybillFormProps) {
  const { showError, showSuccess } = useToastNotification()
  const currentUserId = useAuthStore((state) => state.user?.id)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditMode = !!waybillId
  const [isRateManualEdit, setIsRateManualEdit] = useState(false)
  const [originalRatePerClientValues, setOriginalRatePerClientValues] = useState<RatePerClientDropdownData | null>(null)
  const [hasRateValueChanged, setHasRateValueChanged] = useState(false)
  const [waybillRatePerClientData, setWaybillRatePerClientData] = useState<RatePerClientDropdownData | null>(null)
  const [containerNames, setContainerNames] = useState<string[]>([]) // For create mode: just container numbers
  const [containers, setContainers] = useState<ContainerData[]>([]) // For edit mode: containers with IDs
  const [containerNameInput, setContainerNameInput] = useState<string>('')
  const [isLoadingContainers, setIsLoadingContainers] = useState(false)
  const { isOpen: isLimitModalOpen, onOpen: onLimitModalOpen, onClose: onLimitModalClose } = useDisclosure()
  const [limitModalMessage, setLimitModalMessage] = useState<string>('')
  const { isOpen: isContainerRequiredModalOpen, onOpen: onContainerRequiredModalOpen, onClose: onContainerRequiredModalClose } = useDisclosure()
  const { isOpen: isRemainingContainerModalOpen, onOpen: onRemainingContainerModalOpen, onClose: onRemainingContainerModalClose } = useDisclosure()
  const [pendingContainerToAdd, setPendingContainerToAdd] = useState<string | null>(null)

  // Separate loading states for each API call
  const [isLoadingDrivers, setIsLoadingDrivers] = useState(true)
  const [isLoadingHelpers, setIsLoadingHelpers] = useState(true)
  const [isLoadingFixedExpenses, setIsLoadingFixedExpenses] = useState(false)
  const [isLoadingTrucks, setIsLoadingTrucks] = useState(true)
  const [isLoadingWaybillDetails, setIsLoadingWaybillDetails] = useState(true)
  const [isLoadingRatePerClients, setIsLoadingRatePerClients] = useState(false)

  const [drivers, setDrivers] = useState<DriverDropdownData[]>([])
  const [helpers, setHelpers] = useState<DropdownData[]>([])
  const [fixedExpenses, setFixedExpenses] = useState<ExpenseDropdownData[]>([])
  const [trucks, setTrucks] = useState<TruckDropdownData[]>([])
  const [ratePerClients, setRatePerClients] = useState<RatePerClientDropdownData[]>([])
  const [truckTripExpense, setTruckTripExpense] = useState<{
    id: number
    cash_on_hand: number
    issued_cash_amount: number
    remaining_amount: number
  } | null>(null)
  const [isLoadingTruckTripExpense, setIsLoadingTruckTripExpense] = useState(false)
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
    setValue,
    getValues,
  } = useForm<WaybillFormValues>({
    resolver: zodResolver(isEditMode ? updateWaybillDetailSchema : createWaybillDetailSchema),
    defaultValues: {
      booking_id: bookingId,
      shipping_line_id: shippingLineId,
      actual_truck_trip_expense_amount: 0,
      diesel_expense_amount: 0,
      purchase_order: '',
    },
  })

  const preventNumberInputScroll = (event: WheelEvent<HTMLInputElement>) => {
    event.currentTarget.blur()
  }

  const preventEnterSubmitOnInput = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
    }
  }

  // Fetch containers for edit mode - defined before use in loadData
  // Note: This function is called from within useEffect, so cleanup is handled by the parent effect
  const fetchContainers = async (bookingIdValue: number, waybillIdValue: number) => {
    setIsLoadingContainers(true)
    try {
      const { data } = await apiClient.get('/containers', {
        params: {
          booking_id: bookingIdValue,
          waybill_id: waybillIdValue,
        },
      })
      const containersData = data?.data || data || []
      const formattedContainers = containersData.map((container: any) => ({
        id: container.id || 0,
        container_number: container.container_number || container.container_name || '',
      }))
      setContainers(formattedContainers)
    } catch (error: any) {
      showError(error)
      setContainers([])
    } finally {
      setIsLoadingContainers(false)
    }
  }

  useEffect(() => {
    let isMounted = true

    const loadData = async () => {
      // In edit mode, fetch waybill details first so we have driver/helper/fleet_truck for dropdown injection
      let waybillData: Record<string, any> = {}
      if (isEditMode && waybillId) {
        setIsLoadingWaybillDetails(true)
        try {
          const { data } = await apiClient.get(`/waybill-details/${waybillId}`)
          if (!isMounted) return
          waybillData = data?.data || data || {}
        } catch (error: any) {
          if (isMounted) showError(error)
        } finally {
          if (isMounted) setIsLoadingWaybillDetails(false)
        }
      }

      // Load drivers
      try {
        const driversRes = await apiClient.get('/drivers', { params: { is_active: 1 } })
        if (!isMounted) return
        const driversData = driversRes.data?.data || driversRes.data || []
        // Transform drivers to include helper_id and assigned_truck_plate_numbers
        const transformedDrivers = driversData.map((driver: any) => ({
          id: driver.id,
          full_name: driver.full_name,
          helper_id: driver.helper_id || null,
          assigned_truck_plate_numbers: driver.assigned_truck_plate_numbers || [],
        }))
        // In edit mode: if waybill's driver is not in first fetch, inject from waybill details (driver.first_name + last_name)
        if (waybillData?.driver_id != null && waybillData?.driver && !transformedDrivers.some((d: DriverDropdownData) => d.id === waybillData.driver_id)) {
          const d = waybillData.driver
          const fullName = d.full_name ?? ([d.first_name, d.last_name].filter(Boolean).join(' ').trim() || 'Unknown')
          transformedDrivers.unshift({
            id: waybillData.driver_id,
            full_name: fullName,
            helper_id: d.helper_id ?? null,
            assigned_truck_plate_numbers: d.assigned_truck_plate_numbers ?? [],
          })
        }
        setDrivers(transformedDrivers)
      } catch (error: any) {
        if (isMounted) {
          showError(error)
        }
      } finally {
        if (isMounted) {
          setIsLoadingDrivers(false)
        }
      }

      // Load helpers
      try {
        const helpersRes = await apiClient.get('/helpers', { params: { is_active: 1 } })
        if (!isMounted) return
        let helpersData = (helpersRes.data?.data || helpersRes.data || []) as DropdownData[]
        // In edit mode: if waybill's helper is not in first fetch, inject from waybill details (helper.first_name + last_name)
        if (waybillData?.helper_id != null && waybillData?.helper && !helpersData.some((h: DropdownData) => h.id === waybillData.helper_id)) {
          const h = waybillData.helper
          const fullName = h.full_name ?? ([h.first_name, h.last_name].filter(Boolean).join(' ').trim() || 'Unknown')
          helpersData = [{ id: waybillData.helper_id, full_name: fullName }, ...helpersData]
        }
        setHelpers(helpersData)
      } catch (error: any) {
        if (isMounted) {
          showError(error)
        }
      } finally {
        if (isMounted) {
          setIsLoadingHelpers(false)
        }
      }

      // Load trucks
      try {
        const trucksRes = await apiClient.get('/trucks/truck-list', { params: { is_active: 1 } })
        if (!isMounted) return
        let trucksData = (trucksRes.data?.data || trucksRes.data || []).map((truck: any) => ({
          id: truck.id,
          plate_number: truck.plate_number,
        })) as TruckDropdownData[]
        // In edit mode: if waybill's truck is not in first fetch, inject from waybill details (fleet_truck.plate_number)
        const plateNumber = waybillData?.truck_plate_number ?? waybillData?.fleet_truck?.plate_number
        if (plateNumber && !trucksData.some((t: TruckDropdownData) => t.plate_number === plateNumber)) {
          trucksData = [{ id: plateNumber as any, plate_number: plateNumber }, ...trucksData]
        }
        setTrucks(trucksData)
      } catch (error: any) {
        if (isMounted) {
          showError(error)
        }
      } finally {
        if (isMounted) {
          setIsLoadingTrucks(false)
        }
      }

      // Apply waybill form data and fetch containers (waybillData already fetched at start in edit mode)
      if (isEditMode && waybillId && Object.keys(waybillData).length > 0) {
        const waybillRateData: RatePerClientDropdownData = {
          id: 0,
            rate: String(waybillData.rate || 0),
            stack_run: parseFloat(String(waybillData.stack_run_rate)) || 0,
            no_of_days: waybillData.no_of_days || 1,
            tax_percent: waybillData.tax_percent ?? null,
            has_vat: waybillData.has_vat !== undefined ? waybillData.has_vat : false,
            requirements: waybillData.requirements ?? null,
            remarks: waybillData.remarks ?? null,
          }
          setWaybillRatePerClientData(waybillRateData)

          reset({
            waybill_number: waybillData.waybill_number || '',
            transaction_date: waybillData.transaction_date || '',
            shipping_line_id: waybillData.shipping_line_id || undefined,
            booking_id: waybillData.booking_id || undefined,
            driver_id: waybillData.driver_id || undefined,
            helper_id: waybillData.helper_id || undefined,
            container_size: waybillData.container_size || '',
            container_type: waybillData.container_type || undefined,
            truck_plate_number: waybillData.truck_plate_number || undefined,
            fixed_expense_id: waybillData.fixed_expense_id || undefined,
            pickup_date: waybillData.pickup_date || '',
            delivered_date: waybillData.delivered_date || '',
            no_of_days: waybillData.no_of_days || undefined,
            requirements: waybillData.requirements || undefined,
            remarks: waybillData.remarks || undefined,
            stack_run: waybillData.stack_run_rate || undefined,
            rate: waybillData.rate ? parseFloat(String(waybillData.rate)) : undefined,
            tax_percent: waybillData.tax_percent || undefined,
            has_vat: waybillData.has_vat !== undefined ? waybillData.has_vat : false,
            total_rate_per_client: waybillData.total_rate_per_client || undefined,
            post_expense_amount: waybillData.post_expense_amount || undefined,
            total_expense: waybillData.total_expense || undefined,
            truck_trip_expense_id: waybillData.truck_trip_expense_id || undefined,
            actual_truck_trip_expense_amount:
              waybillData.actual_truck_trip_expense_amount !== undefined &&
              waybillData.actual_truck_trip_expense_amount !== null
                ? waybillData.actual_truck_trip_expense_amount
                : 0,
            diesel_expense_id: waybillData.diesel_expense_id || undefined,
            diesel_expense_amount:
              waybillData.diesel_expense_amount !== undefined && waybillData.diesel_expense_amount !== null
                ? waybillData.diesel_expense_amount
                : 0,
            purchase_order: waybillData.purchase_order || '',
          })

        setIsRateManualEdit(true)

        if (waybillData.booking_id) {
          await fetchContainers(waybillData.booking_id, waybillId)
        }
      } else if (bookingId && shippingLineId) {
        // Pre-fill booking_id and shipping_line_id when creating from a specific booking
        reset({
          booking_id: bookingId,
          shipping_line_id: shippingLineId,
        })
      }
    }

    loadData()

    return () => {
      isMounted = false
    }
  }, [isEditMode, waybillId, bookingId, shippingLineId, reset, showError])

  // Watch container_size to trigger fixed expense fetching
  const containerSize = watch('container_size')
  const shippingLineIdValue = watch('shipping_line_id') || shippingLineId
  const fixedExpenseId = watch('fixed_expense_id')
  const postExpenseAmount = watch('post_expense_amount') || 0
  const rateId = watch('rate')
  const driverId = watch('driver_id')
  const helperId = watch('helper_id')
  const truckPlateNumber = watch('truck_plate_number')
  const dieselExpenseId = watch('diesel_expense_id')
  const containerCount = isEditMode ? containers.length : containerNames.length
  const containerMultiplier = containerCount > 1 ? containerCount : 1

  // Check if all required data is loaded before enabling dropdowns
  const isDriverHelperTruckDataLoaded = !isLoadingDrivers && !isLoadingHelpers && !isLoadingTrucks

  // Helper function to map offhire variants to regular sizes for filtering
  const getContainerSizeForFiltering = (size: string | undefined): string | undefined => {
    if (!size) return undefined
    if (size === '20ft(offhire)') return '20ft'
    if (size === '40ft(offhire)') return '40ft'
    return size
  }

  // Get container size for filtering (maps offhire variants to regular sizes)
  const containerSizeForFiltering = getContainerSizeForFiltering(containerSize)

  // Get selected fixed expense data (only after list is fetched)
  const selectedFixedExpense = !isLoadingFixedExpenses && fixedExpenses.length > 0 && fixedExpenseId
    ? fixedExpenses.find((expense) => expense.id === fixedExpenseId)
    : null
  
  // Get selected rate per client data
  // In edit mode, use waybill-details data; otherwise use rate-per-client API data
  const selectedRatePerClient = isEditMode && waybillRatePerClientData
    ? waybillRatePerClientData
    : (!isLoadingRatePerClients && ratePerClients.length > 0 && rateId
      ? ratePerClients.find((rate) => rate.id === rateId)
      : null)

  // Calculate total rate per client based on rate value
  // In edit mode, use waybill-details total_rate_per_client; otherwise calculate from rate
  const calculatedTotalRatePerClient = (() => {
    // In edit mode, use waybill-details total_rate_per_client only when not manually editing rate.
    // When manually editing (edit mode default), recalculate from current rate and container count.
    if (isEditMode && waybillRatePerClientData && !isRateManualEdit) {
      const waybillTotalRate = getValues('total_rate_per_client')
      if (waybillTotalRate !== undefined && waybillTotalRate !== null) {
        return parseFloat(String(waybillTotalRate)) || 0
      }
    }
    
    if (isRateManualEdit) {
      // In manual edit mode, rateId is the numeric value
      const baseRate = rateId ? (typeof rateId === 'number' ? rateId : parseFloat(String(rateId)) || 0) : 0
      return baseRate * containerMultiplier
    } else {
      // In dropdown mode, only calculate if rate per clients are loaded and we have a selection
      if (isLoadingRatePerClients || ratePerClients.length === 0) {
        return 0
      }
      if (selectedRatePerClient && selectedRatePerClient.rate) {
        const baseRate = parseFloat(String(selectedRatePerClient.rate)) || 0
        return baseRate * containerMultiplier
      }
      return 0
    }
  })()

  // Calculate total expense: fixed expense + post expense amount (only if fixed expense is selected)
  // Only calculate after fixed expenses are fetched
  const calculatedTotalExpense = (() => {
    if (isLoadingFixedExpenses || fixedExpenses.length === 0) {
      return 0
    }
    if (selectedFixedExpense && selectedFixedExpense.total_expenses) {
      const fixedExpenseValue = parseFloat(String(selectedFixedExpense.total_expenses)) || 0
      const postExpense = typeof postExpenseAmount === 'number' ? postExpenseAmount : (parseFloat(String(postExpenseAmount)) || 0)
      const baseTotalExpense = fixedExpenseValue + postExpense
      return baseTotalExpense * containerMultiplier
    }
    return 0
  })()

  // Fetch fixed expenses when container_size, shipping_line_id, cypaFromId, or cypaToId changes
  useEffect(() => {
    let isMounted = true

    const fetchFixedExpenses = async () => {
      // Only fetch if we have all required filters
      if (!containerSize || !shippingLineIdValue || !cypaFromId || !cypaToId) {
        if (isMounted) {
          setFixedExpenses([])
          // Clear selected fixed_expense_id and post_expense_amount when filters are incomplete
          const currentFixedExpenseId = getValues('fixed_expense_id')
          if (currentFixedExpenseId) {
            setValue('fixed_expense_id', undefined as any, { shouldValidate: false })
            setValue('post_expense_amount', 0, { shouldValidate: false })
          }
        }
        return
      }

      setIsLoadingFixedExpenses(true)
      try {
        const fixedExpensesRes = await apiClient.get('/fixed-expenses', {
          params: {
            shipping_line_id: shippingLineIdValue,
            cypa_id_from: cypaFromId,
            cypa_id_to: cypaToId,
            container_size: containerSizeForFiltering,
          },
        })
        if (!isMounted) return
        const fixedExpensesData = fixedExpensesRes.data?.data || fixedExpensesRes.data || []
        // Transform fixed expenses to display format
        const transformedExpenses = fixedExpensesData.map((expense: any) => ({
          id: expense.id,
          total_expenses: expense.total_expenses,
          expenses: parseFloat(String(expense.expenses)) || 0,
          stack_run: parseFloat(String(expense.stack_run)) || 0,
          docs_fee: parseFloat(String(expense.docs_fee)) || 0,
          online_booking_fee: parseFloat(String(expense.online_booking_fee)) || 0,
        }))
        setFixedExpenses(transformedExpenses)

        // Get current selected fixed_expense_id
        const currentFixedExpenseId = getValues('fixed_expense_id')
        
        // Check if current selection is still valid
        const isCurrentSelectionValid = currentFixedExpenseId && transformedExpenses.find((e: any) => e.id === currentFixedExpenseId)
        
        if (isCurrentSelectionValid) {
          // Keep the current selection if it's still in the list
          // No need to change anything
        } else if (transformedExpenses.length === 1) {
          // Auto-select if only one expense is fetched
          setValue('fixed_expense_id', transformedExpenses[0].id, { shouldValidate: false })
        } else {
          // Clear selection if current selection is not valid and there are multiple options
          if (currentFixedExpenseId) {
            setValue('fixed_expense_id', undefined as any, { shouldValidate: false })
            setValue('post_expense_amount', 0, { shouldValidate: false })
          }
        }
      } catch (error: any) {
        if (isMounted) {
          showError(error)
          setFixedExpenses([])
          // Clear selected fixed_expense_id on error
          const currentFixedExpenseId = getValues('fixed_expense_id')
          if (currentFixedExpenseId) {
            setValue('fixed_expense_id', undefined as any, { shouldValidate: false })
            setValue('post_expense_amount', 0, { shouldValidate: false })
          }
        }
      } finally {
        if (isMounted) {
          setIsLoadingFixedExpenses(false)
        }
      }
    }

    fetchFixedExpenses()

    return () => {
      isMounted = false
    }
  }, [containerSize, containerSizeForFiltering, shippingLineIdValue, cypaFromId, cypaToId, showError, setValue, getValues])

  // Fetch rate per clients when container_size, shipping_line_id, or cypaToId changes
  useEffect(() => {
    let isMounted = true

    const fetchRatePerClients = async () => {
      // In edit mode, don't fetch rate-per-client API data - use waybill-details data instead
      if (isEditMode) {
        return
      }
      
      // Reset manual edit mode when filters change
      if (isMounted) {
        setIsRateManualEdit(false)
      }
      
      // Only fetch if we have all required filters
      if (!containerSize || !shippingLineIdValue || !cypaToId) {
        if (isMounted) {
          setRatePerClients([])
          // Clear selected rate when filters are incomplete
          const currentRateId = getValues('rate')
          if (currentRateId) {
            setValue('rate', undefined as any, { shouldValidate: false })
          }
        }
        return
      }

      setIsLoadingRatePerClients(true)
      try {
        const ratePerClientsRes = await apiClient.get('/rate-per-clients', {
          params: {
            shipping_line_id: shippingLineIdValue,
            cypa_id: cypaToId,
            container_size: containerSizeForFiltering,
          },
        })
        if (!isMounted) return
        const ratePerClientsData = ratePerClientsRes.data?.data || ratePerClientsRes.data || []
        // Transform rate per clients to display format
        const transformedRates = ratePerClientsData.map((rate: any) => ({
          id: rate.id,
          rate: rate.rate,
          stack_run: parseFloat(String(rate.stack_run)) || 0,
          no_of_days: rate.no_of_days || 1,
          tax_percent: rate.tax_percent || null,
          has_vat: rate.has_vat !== undefined ? rate.has_vat : false,
          requirements: rate.requirements || null,
          remarks: rate.remarks || null,
        }))
        setRatePerClients(transformedRates)

        // Get current selected rate
        const currentRateId = getValues('rate')
        
        // Check if current selection is still valid
        const isCurrentSelectionValid = currentRateId && transformedRates.find((r: any) => r.id === currentRateId)
        
        if (isCurrentSelectionValid) {
          // Keep the current selection if it's still in the list
          // No need to change anything
        } else if (transformedRates.length === 1) {
          // Auto-select if only one rate is fetched
          setValue('rate', transformedRates[0].id, { shouldValidate: false })
        } else {
          // Clear selection if current selection is not valid and there are multiple options
          if (currentRateId) {
            setValue('rate', undefined as any, { shouldValidate: false })
          }
        }
      } catch (error: any) {
        if (isMounted) {
          showError(error)
          setRatePerClients([])
          // Clear selected rate on error
          const currentRateId = getValues('rate')
          if (currentRateId) {
            setValue('rate', undefined as any, { shouldValidate: false })
          }
        }
      } finally {
        if (isMounted) {
          setIsLoadingRatePerClients(false)
        }
      }
    }

    fetchRatePerClients()

    return () => {
      isMounted = false
    }
  }, [containerSize, containerSizeForFiltering, shippingLineIdValue, cypaToId, isEditMode, showError, setValue, getValues])

  // Clear post expense amount when fixed expense is cleared
  useEffect(() => {
    if (!fixedExpenseId && getValues('post_expense_amount')) {
      setValue('post_expense_amount', 0, { shouldValidate: false })
    }
  }, [fixedExpenseId, setValue, getValues])

  // Auto-select helper and truck based on selected driver (create mode only)
  useEffect(() => {
    // Only run in create mode and when all data is loaded
    if (isEditMode || !isDriverHelperTruckDataLoaded) {
      return
    }

    // If driver is cleared, clear helper and truck
    if (!driverId) {
      setValue('helper_id', undefined as any, { shouldValidate: false })
      setValue('truck_plate_number', undefined as any, { shouldValidate: false })
      return
    }

    // Find the selected driver
    const selectedDriver = drivers.find((driver) => driver.id === driverId)
    if (!selectedDriver) {
      // Clear helper and truck if driver not found
      setValue('helper_id', undefined as any, { shouldValidate: false })
      setValue('truck_plate_number', undefined as any, { shouldValidate: false })
      return
    }

    // Auto-select helper if driver has helper_id
    if (selectedDriver.helper_id) {
      const helperExists = helpers.some((helper) => helper.id === selectedDriver.helper_id)
      if (helperExists) {
        setValue('helper_id', selectedDriver.helper_id, { shouldValidate: false })
      } else {
        // Clear helper if not found
        setValue('helper_id', undefined as any, { shouldValidate: false })
      }
    } else {
      // Clear helper if driver has no helper_id
      setValue('helper_id', undefined as any, { shouldValidate: false })
    }

    // Auto-select truck if driver has assigned truck plate numbers
    if (selectedDriver.assigned_truck_plate_numbers && selectedDriver.assigned_truck_plate_numbers.length > 0) {
      // Find the first truck plate number that exists in trucks list
      const matchingTruck = trucks.find((truck) =>
        selectedDriver.assigned_truck_plate_numbers?.includes(truck.plate_number)
      )
      if (matchingTruck) {
        setValue('truck_plate_number', matchingTruck.plate_number, { shouldValidate: false })
      } else {
        // Clear truck if no match found
        setValue('truck_plate_number', undefined as any, { shouldValidate: false })
      }
    } else {
      // Clear truck if driver has no assigned trucks
      setValue('truck_plate_number', undefined as any, { shouldValidate: false })
    }
  }, [driverId, drivers, helpers, trucks, isDriverHelperTruckDataLoaded, isEditMode, setValue])

  // In edit mode, verify and match helper and truck based on waybill-details data
  useEffect(() => {
    // Only run in edit mode and when all data is loaded
    if (!isEditMode || !isDriverHelperTruckDataLoaded) {
      return
    }

    const currentDriverId = getValues('driver_id')
    const currentHelperId = getValues('helper_id')
    const currentTruckPlateNumber = getValues('truck_plate_number')

    // Verify helper exists in helpers list
    if (currentHelperId) {
      const helperExists = helpers.some((helper) => helper.id === currentHelperId)
      if (!helperExists) {
        // Clear if helper not found
        setValue('helper_id', undefined as any, { shouldValidate: false })
      }
    }

    // Verify truck exists in trucks list
    if (currentTruckPlateNumber) {
      const truckExists = trucks.some((truck) => truck.plate_number === currentTruckPlateNumber)
      if (!truckExists) {
        // Clear if truck not found
        setValue('truck_plate_number', undefined as any, { shouldValidate: false })
      }
    }
  }, [isEditMode, isDriverHelperTruckDataLoaded, helpers, trucks, getValues, setValue])

  // Fetch latest truck trip expense whenever plate number or helper id change (create and edit mode)
  useEffect(() => {
    let isMounted = true

    const fetchTruckTripExpense = async () => {
      // Clear when either value is missing
      if (!helperId || !truckPlateNumber) {
        if (isMounted) {
          setTruckTripExpense(null)
          setValue('truck_trip_expense_id', undefined as any, { shouldValidate: false })
          setValue('actual_truck_trip_expense_amount', 0, { shouldValidate: false })
        }
        return
      }

      setIsLoadingTruckTripExpense(true)
      try {
        const { data } = await apiClient.get('/budget/truck-trip-expense/latest', {
          params: {
            plate_number: truckPlateNumber,
            helper_id: helperId,
          },
        })

        if (!isMounted) return

        const expense = data?.data || data || null
        if (expense && expense.id) {
          const cashOnHand = parseFloat(String(expense.cash_on_hand ?? 0)) || 0
          const issuedCashAmount = parseFloat(String(expense.issued_cash_amount ?? 0)) || 0
          const remainingAmount = parseFloat(String(expense.remaining_amount ?? 0)) || 0

          setTruckTripExpense({
            id: expense.id,
            cash_on_hand: cashOnHand,
            issued_cash_amount: issuedCashAmount,
            remaining_amount: remainingAmount,
          })

          setValue('truck_trip_expense_id', expense.id, { shouldValidate: true })
          // In edit mode keep existing actual amount from waybill; in create mode default to 0
          const currentAmount = getValues('actual_truck_trip_expense_amount')
          const nextAmount =
            isEditMode && currentAmount !== undefined && currentAmount !== null && !Number.isNaN(currentAmount)
              ? currentAmount
              : 0
          setValue('actual_truck_trip_expense_amount', nextAmount, { shouldValidate: true })
        } else {
          setTruckTripExpense(null)
          setValue('truck_trip_expense_id', undefined as any, { shouldValidate: false })
          setValue('actual_truck_trip_expense_amount', 0, { shouldValidate: false })
        }
      } catch (error: any) {
        // If API fails (404, 422, 500, etc.), just hide truck trip expense section
        if (isMounted) {
          setTruckTripExpense(null)
          setValue('truck_trip_expense_id', undefined as any, { shouldValidate: false })
          setValue('actual_truck_trip_expense_amount', 0, { shouldValidate: false })
        }
      } finally {
        if (isMounted) {
          setIsLoadingTruckTripExpense(false)
        }
      }
    }

    fetchTruckTripExpense()

    return () => {
      isMounted = false
    }
  }, [helperId, truckPlateNumber, setValue, getValues, isEditMode])

  // Store original rate per client values
  // In edit mode, use waybill-details data; in create mode, use selected rate per client from API
  useEffect(() => {
    if (isEditMode && waybillRatePerClientData) {
      // In edit mode, store waybill-details data as original values
      setOriginalRatePerClientValues(waybillRatePerClientData)
      setHasRateValueChanged(false)
    } else if (!isRateManualEdit && selectedRatePerClient) {
      // In create mode, store selected rate per client from API
      setOriginalRatePerClientValues(selectedRatePerClient)
      setHasRateValueChanged(false)
      
      // Pre-fill fields from selected rate per client
      if (selectedRatePerClient.tax_percent !== null && selectedRatePerClient.tax_percent !== undefined) {
        setValue('tax_percent', selectedRatePerClient.tax_percent, { shouldValidate: false })
      }
      if (selectedRatePerClient.has_vat !== undefined) {
        setValue('has_vat', selectedRatePerClient.has_vat, { shouldValidate: false })
      }
      if (selectedRatePerClient.no_of_days !== undefined) {
        setValue('no_of_days', selectedRatePerClient.no_of_days, { shouldValidate: false })
      }
      if (selectedRatePerClient.stack_run !== undefined) {
        setValue('stack_run', selectedRatePerClient.stack_run, { shouldValidate: false })
      }
      if (selectedRatePerClient.requirements !== null && selectedRatePerClient.requirements !== undefined) {
        setValue('requirements', selectedRatePerClient.requirements, { shouldValidate: false })
      }
      if (selectedRatePerClient.remarks !== null && selectedRatePerClient.remarks !== undefined) {
        setValue('remarks', selectedRatePerClient.remarks, { shouldValidate: false })
      }
    }
  }, [selectedRatePerClient, isRateManualEdit, isEditMode, waybillRatePerClientData, setValue])

  // Track if rate value has changed in manual edit mode
  useEffect(() => {
    if (isRateManualEdit && originalRatePerClientValues) {
      const currentRateValue = getValues('rate')
      const originalRateValue = parseFloat(originalRatePerClientValues.rate) || 0
      const hasChanged = currentRateValue !== undefined && currentRateValue !== null && 
                        Math.abs((typeof currentRateValue === 'number' ? currentRateValue : parseFloat(String(currentRateValue)) || 0) - originalRateValue) > 0.01
      setHasRateValueChanged(hasChanged)
    }
  }, [rateId, isRateManualEdit, originalRatePerClientValues, getValues])


  const proceedWithAddContainer = (containerNumber: string) => {
    // Get current container count
    const currentCount = isEditMode ? containers.length : containerNames.length
    const containerSizeValue = containerSize

    // Determine max containers based on container size
    let maxContainers = 0
    
    if (containerSizeValue === '20ft' || containerSizeValue === '20ft(offhire)') {
      maxContainers = 2
    } else if (containerSizeValue === '40ft' || containerSizeValue === '40ft(offhire)') {
      maxContainers = 1
    }

    // Check if limit is exceeded
    if (currentCount >= maxContainers) {
      setLimitModalMessage(
        `The maximum number of containers for ${containerSizeValue} container size is ${maxContainers}. You cannot add more containers.`
      )
      onLimitModalOpen()
      return
    }

    // Add container if within limit
    if (isEditMode) {
      // In edit mode, add to containers array (will be created on submit)
      setContainers([...containers, { id: 0, container_number: containerNumber }])
    } else {
      // In create mode, add to containerNames array
      setContainerNames([...containerNames, containerNumber])
    }
    setContainerNameInput('')
  }

  const handleAddContainer = async () => {
    if (!containerNameInput.trim()) {
      return
    }

    const containerSizeValue = containerSize

    // Check if container size is selected
    if (!containerSizeValue) {
      setLimitModalMessage('Please select a container size first before adding containers.')
      onLimitModalOpen()
      return
    }

    // Check remaining containers before proceeding
    if (bookingId) {
      try {
        const response = await apiClient.get(`/bookings/${bookingId}/remaining-container`)
        const remainingContainer = response.data?.remaining_container ?? response.data?.data?.remaining_container ?? null

        if (remainingContainer !== null && remainingContainer <= 0) {
          // Store the container to add if user confirms
          setPendingContainerToAdd(containerNameInput.trim())
          onRemainingContainerModalOpen()
          return
        }
      } catch (error: any) {
        // If API fails, proceed with normal flow (don't block user)
        showError(error)
      }
    }

    // Proceed with adding container if remaining_container > 0 or bookingId is not available
    proceedWithAddContainer(containerNameInput.trim())
  }

  const handleConfirmAddContainer = () => {
    if (pendingContainerToAdd) {
      proceedWithAddContainer(pendingContainerToAdd)
      setPendingContainerToAdd(null)
    }
    onRemainingContainerModalClose()
  }

  const handleCancelAddContainer = () => {
    setPendingContainerToAdd(null)
    onRemainingContainerModalClose()
  }

  const handleDeleteContainer = async (index: number) => {
    if (isEditMode) {
      // In edit mode, check if container has an ID (existing container from API)
      const container = containers[index]
      if (container.id && container.id > 0 && bookingId) {
        // Delete from API
        try {
          await apiClient.delete(`/bookings/${bookingId}/containers/${container.id}`)
          // Remove from local state
          setContainers(containers.filter((_, i) => i !== index))
          showSuccess('Success', 'Container deleted successfully')
        } catch (error: any) {
          showError(error)
        }
      } else {
        // New container not yet saved, just remove from local state
        setContainers(containers.filter((_, i) => i !== index))
      }
    } else {
      // In create mode, just remove from local state
      setContainerNames(containerNames.filter((_, i) => i !== index))
    }
  }

  const createContainers = async (createdWaybillId: number | undefined) => {
    // In edit mode, only create containers that don't have an ID (new containers)
    // In create mode, create all containers from containerNames
    let containersToCreate: string[] = []
    
    if (isEditMode) {
      // Filter out containers that already have IDs (already exist in DB)
      containersToCreate = containers
        .filter((c) => !c.id || c.id === 0)
        .map((c) => c.container_number)
    } else {
      containersToCreate = containerNames
    }

    // If no containers to create or no bookingId, skip container creation
    if (!containersToCreate || containersToCreate.length === 0 || !bookingId || !createdWaybillId) {
      return true // Return true to indicate success (nothing to do)
    }

    try {
      const containerPromises = containersToCreate.map((containerNumber) =>
        apiClient.post(`/bookings/${bookingId}/containers`, {
          booking_id: bookingId,
          waybill_id: createdWaybillId,
          container_number: containerNumber,
        })
      )

      await Promise.all(containerPromises)
      return true // Success
    } catch (error: any) {
      throw error // Re-throw to be handled by caller
    }
  }

  const onSubmit = async (data: WaybillFormValues) => {
    // Validate containers before proceeding
    const hasContainers = isEditMode
      ? containers.length > 0
      : (containerNames && containerNames.length > 0)

    if (!hasContainers) {
      onContainerRequiredModalOpen()
      return
    }

    setIsSubmitting(true)

    try {
      // Get rate value: if manual edit mode, use the value directly; otherwise get from selected rate per client
      let rateValue = 0
      if (isRateManualEdit) {
        // In manual edit mode, data.rate is already the numeric value
        rateValue = data.rate || 0
      } else {
        // In dropdown mode, data.rate is the ID, so get the actual rate value
        rateValue = selectedRatePerClient ? parseFloat(selectedRatePerClient.rate) || 0 : 0
      }

      // Get no_of_days and convert to integer
      const noOfDaysValue = isRateManualEdit 
        ? (data.no_of_days ?? 1)
        : (originalRatePerClientValues?.no_of_days ?? selectedRatePerClient?.no_of_days ?? 1)
      const noOfDaysInt = Math.floor(parseFloat(String(noOfDaysValue)) || 1)

      // Convert total_rate_per_client to float
      const totalRatePerClientFloat = calculatedTotalRatePerClient 
        ? parseFloat(String(calculatedTotalRatePerClient)) || 0 
        : 0

      // Convert total_expense to float
      const totalExpenseFloat = typeof calculatedTotalExpense === 'number' 
        ? parseFloat(String(calculatedTotalExpense)) || 0 
        : 0
      
      const stackRunValue = isRateManualEdit
        ? (data.stack_run ?? 0)
        : (originalRatePerClientValues?.stack_run ?? selectedRatePerClient?.stack_run ?? 0)

      const payload = {
        waybill_number: data.waybill_number,
        transaction_date: data.transaction_date,
        shipping_line_id: data.shipping_line_id,
        booking_id: data.booking_id,
        driver_id: data.driver_id,
        helper_id: data.helper_id || null,
        container_size: data.container_size,
        container_type: data.container_type || null,
        truck_plate_number: data.truck_plate_number,
        fixed_expense_id: data.fixed_expense_id,
        pickup_date: data.pickup_date,
        delivered_date: data.delivered_date,
        no_of_days: noOfDaysInt,
        requirements: isRateManualEdit 
          ? (data.requirements || null)
          : (originalRatePerClientValues?.requirements ?? selectedRatePerClient?.requirements ?? null),
        remarks: isRateManualEdit 
          ? (data.remarks || null)
          : (originalRatePerClientValues?.remarks ?? selectedRatePerClient?.remarks ?? null),
        stack_run: stackRunValue,
        rate: rateValue,
        tax_percent: 0,
        has_vat: isRateManualEdit 
          ? (data.has_vat !== undefined ? data.has_vat : false)
          : (originalRatePerClientValues?.has_vat !== undefined ? originalRatePerClientValues.has_vat : (selectedRatePerClient?.has_vat !== undefined ? selectedRatePerClient.has_vat : false)),
        total_rate_per_client: totalRatePerClientFloat,
        post_expense_amount: selectedFixedExpense ? (data.post_expense_amount ?? 0) : 0,
        total_expense: totalExpenseFloat,
        truck_trip_expense_id: data.truck_trip_expense_id ?? null,
        actual_truck_trip_expense_amount:
          data.actual_truck_trip_expense_amount !== undefined && data.actual_truck_trip_expense_amount !== null
            ? data.actual_truck_trip_expense_amount
            : 0,
        diesel_expense_id: isEditMode ? data.diesel_expense_id ?? null : undefined,
        diesel_expense_amount:
          data.diesel_expense_amount !== undefined && data.diesel_expense_amount !== null
            ? data.diesel_expense_amount
            : null,
        purchase_order: data.purchase_order || null,
        prepared_by: currentUserId ?? undefined,
      }

      let createdWaybillId: number | undefined
      if (isEditMode) {
        const response = await apiClient.put(`/waybill-details/${waybillId}`, payload)
        createdWaybillId = waybillId
      } else {
        const response = await apiClient.post('/waybill-details', payload)
        createdWaybillId = response.data?.data?.id || response.data?.id
      }

      // Create containers after successful waybill creation/update
      // Only proceed if createdWaybillId is defined
      if (createdWaybillId) {
        // Check if there are containers to create
        const hasContainersToCreate = isEditMode
          ? containers.some((c) => !c.id || c.id === 0)
          : (containerNames && containerNames.length > 0)

        if (hasContainersToCreate) {
          try {
            await createContainers(createdWaybillId)
            // If container creation succeeds, show success message
            showSuccess('Success', isEditMode ? 'Waybill updated successfully' : 'Waybill created successfully')
            if (onSuccess) {
              onSuccess()
            }
          } catch (error: any) {
            // If container creation fails, show error
            showError(error)
          }
        } else {
          // No containers to create, just show success for waybill creation
          showSuccess('Success', isEditMode ? 'Waybill updated successfully' : 'Waybill created successfully')
          if (onSuccess) {
            onSuccess()
          }
        }
      } else {
        // If no waybill ID was created, just call onSuccess
        if (onSuccess) {
          onSuccess()
        }
      }
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    }
  }

  // Check if any critical data is still loading (for edit mode, wait for waybill details)
  const isFormDisabled = isEditMode && isLoadingWaybillDetails

  if (isFormDisabled) {
    return (
        <Box p={4}>
          <Card>
            <CardBody>
              <VStack spacing={4} py={8}>
                <Text>Loading waybill details...</Text>
              </VStack>
            </CardBody>
          </Card>
        </Box>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={6} align="stretch">
        {/* Waybill Information Section */}
        <Box>
          <Text fontSize="md" fontWeight="semibold" color="gray.500" mb={4}>
            Waybill Information
          </Text>
          <VStack spacing={4} align="stretch">
            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4}>
              <FormControl isInvalid={!!errors.waybill_number}>
                <FormLabel fontWeight="medium">
                  Waybill Number <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <Input
                  {...register('waybill_number')}
                  onKeyDown={preventEnterSubmitOnInput}
                  placeholder="Enter waybill number"
                  _placeholder={{ fontSize: 'sm' }}
                  isDisabled={isFormDisabled}
                />
                <FormErrorMessage>{errors.waybill_number?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.transaction_date}>
                <FormLabel fontWeight="medium">
                  Transaction Date <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <Input
                  type="date"
                  {...register('transaction_date')}
                  onKeyDown={preventEnterSubmitOnInput}
                  placeholder="Select transaction date"
                  _placeholder={{ fontSize: 'sm' }}
                  isDisabled={isFormDisabled}
                />
                <FormErrorMessage>{errors.transaction_date?.message}</FormErrorMessage>
              </FormControl>
            </Grid>
          </VStack>
        </Box>

        <Box height={1} />
        <Divider borderColor="gray.300" />

        {/* Shipping Line Information Section */}
        <Box>
          <Text fontSize="md" fontWeight="semibold" color="gray.500" mb={4}>
            Booking & Shipping Information
          </Text>
          <VStack spacing={6} align="stretch">
            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4}>
              {/* Shipping Line Name */}
              <Box>
                <Text fontSize="md" fontWeight="medium" mb={1}>
                  Shipping Line Name
                </Text>
                <Text fontSize="md" color="gray.500">
                  {shippingLineName || 'N/A'}
                </Text>
              </Box>

              {/* Booking Reference Number */}
              <Box>
                <Text fontSize="md" fontWeight="medium" mb={1}>
                  Booking Reference Number
                </Text>
                <Text fontSize="md" color="gray.500">
                  {bookingReferenceNumber || 'N/A'}
                </Text>
              </Box>
            </Grid>

            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4}>
              {/* Received From */}
              <Box>
                <Text fontSize="md" fontWeight="medium" mb={1}>
                  Pickup
                </Text>
                <Text fontSize="md" color="gray.500">
                  {cypaFromName || 'N/A'}
                </Text>
              </Box>

              {/* Delivered To */}
              <Box>
                <Text fontSize="md" fontWeight="medium" mb={1}>
                  Dropoff
                </Text>
                <Text fontSize="md" color="gray.500">
                  {cypaToName || 'N/A'}
                </Text>
              </Box>
            </Grid>
          </VStack>
        </Box>

        <Box height={1} />
        <Divider borderColor="gray.300" />

        {/* Driver and Helper Section */}
        <Box>
          <Text fontSize="md" fontWeight="semibold" color="gray.500" mb={4}>
            Driver & Helper Information
          </Text>
          <VStack spacing={4} align="stretch">
            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4}>
              <FormControl isInvalid={!!errors.driver_id}>
                <FormLabel fontWeight="medium">
                  Driver <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <Controller
                  name="driver_id"
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      options={drivers.map((driver) => ({
                        id: driver.id,
                        name: driver.full_name,
                      }))}
                      optionsUrl="/drivers"
                      optionsParams={{ is_active: 1 }}
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      placeholder="Select Driver"
                      isLoading={isLoadingDrivers}
                      isDisabled={!isDriverHelperTruckDataLoaded || isFormDisabled}
                      isInvalid={!!errors.driver_id}
                    />
                  )}
                />  
                <FormErrorMessage>{errors.driver_id?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.helper_id}>
                <FormLabel fontWeight="medium">
                  Helper <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <Controller
                  name="helper_id"
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      options={helpers.map((helper) => ({
                        id: helper.id,
                        name: helper.full_name,
                      }))}
                      optionsUrl="/helpers"
                      optionsParams={{ is_active: 1 }}
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      placeholder="Select Helper"
                      isLoading={isLoadingHelpers}
                      isDisabled={!isDriverHelperTruckDataLoaded || isFormDisabled}
                      isInvalid={!!errors.helper_id}
                    />
                  )}
                />
                <FormErrorMessage>{errors.helper_id?.message}</FormErrorMessage>
              </FormControl>
            </Grid>

            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4}>
              <FormControl isInvalid={!!errors.truck_plate_number}>
                <FormLabel fontWeight="medium">
                  Truck Plate Number <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <Controller
                  name="truck_plate_number"
                  control={control}
                  render={({ field }) => (
                    <SearchableSelect
                      options={trucks.map((truck) => ({
                        id: truck.plate_number,
                        name: truck.plate_number,
                      }))}
                      optionsUrl="/trucks/truck-list"
                      optionsParams={{ is_active: 1 }}
                      optionIdKey="plate_number"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      placeholder="Select Truck Plate Number"
                      isLoading={isLoadingTrucks}
                      isDisabled={!isDriverHelperTruckDataLoaded || isFormDisabled}
                      isInvalid={!!errors.truck_plate_number}
                    />
                  )}
                />
                <FormErrorMessage>{errors.truck_plate_number?.message}</FormErrorMessage>
              </FormControl>
            </Grid>
          </VStack>
        </Box>

        <Box height={1} />
        <Divider borderColor="gray.300" />

        {/* Rates and Expenses Section */}
        <Box>
          <Text fontSize="md" fontWeight="semibold" color="gray.500" mb={4}>
            Rates & Expenses Information
          </Text>
          <VStack spacing={4} align="stretch">
            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4}>
              <FormControl isInvalid={!!errors.container_size}>
                <FormLabel fontWeight="medium">
                  Container Size <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <Controller
                  name="container_size"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select Container Size"
                      isDisabled={isEditMode || isFormDisabled}
                    >
                      <option value="20ft">20ft</option>
                      <option value="40ft">40ft</option>
                      <option value="20ft(offhire)">20ft(offhire)</option>
                      <option value="40ft(offhire)">40ft(offhire)</option>
                    </Select>
                  )}
                />
                <FormErrorMessage>{errors.container_size?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.container_type}>
                <FormLabel fontWeight="medium">Container Type</FormLabel>
                <Input
                  {...register('container_type')}
                  onKeyDown={preventEnterSubmitOnInput}
                  placeholder="Enter container type"
                  _placeholder={{ fontSize: 'sm' }}
                  isDisabled={isFormDisabled}
                />
                <FormErrorMessage>{errors.container_type?.message}</FormErrorMessage>
              </FormControl>
            </Grid>

            <Text fontSize="sm" color="gray.400" fontStyle="italic">
              * Select a container size first to fetch the fixed expenses and rate per clients
            </Text>

            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4}>
              <FormControl isInvalid={!!errors.fixed_expense_id}>
                <FormLabel fontWeight="medium">
                  Fixed Expense <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <Controller
                  name="fixed_expense_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                      placeholder="Select Fixed Expense"
                      isDisabled={isLoadingFixedExpenses || isFormDisabled || !containerSize}
                      isInvalid={!!errors.fixed_expense_id}
                    >
                      {fixedExpenses.map((expense) => (
                        <option key={expense.id} value={expense.id}>
                          {expense.total_expenses}
                        </option>
                      ))}
                    </Select>
                  )}
                />
                <FormErrorMessage>{errors.fixed_expense_id?.message}</FormErrorMessage>
              </FormControl>
            </Grid>

            {selectedFixedExpense && (
              <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4}>
                <Box>
                  <Text fontSize="md" fontWeight="medium" fontStyle="italic" color="gray.500" mb={2}>
                    Fixed Expense Breakdown
                  </Text>
                  <TableContainer>
                    <Table variant="simple" size="sm" borderWidth="1px" borderColor="gray.300">
                      <Thead bg="gray.100">
                        <Tr>
                          <Th borderWidth="1px" borderColor="gray.300">Expense</Th>
                          <Th borderWidth="1px" borderColor="gray.300" textAlign="right">Amount</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        <Tr>
                          <Td borderWidth="1px" borderColor="gray.300">Expenses</Td>
                          <Td borderWidth="1px" borderColor="gray.300" textAlign="right">
                            {selectedFixedExpense.expenses.toFixed(2)}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td borderWidth="1px" borderColor="gray.300">Stack Run</Td>
                          <Td borderWidth="1px" borderColor="gray.300" textAlign="right">
                            {selectedFixedExpense.stack_run.toFixed(2)}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td borderWidth="1px" borderColor="gray.300">Docs Fee</Td>
                          <Td borderWidth="1px" borderColor="gray.300" textAlign="right">
                            {selectedFixedExpense.docs_fee.toFixed(2)}
                          </Td>
                        </Tr>
                        <Tr>
                          <Td borderWidth="1px" borderColor="gray.300">Online Booking Fee</Td>
                          <Td borderWidth="1px" borderColor="gray.300" textAlign="right">
                            {selectedFixedExpense.online_booking_fee.toFixed(2)}
                          </Td>
                        </Tr>
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              </Grid>
            )}

            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4}>
              <FormControl isInvalid={!!errors.post_expense_amount}>
                <FormLabel fontWeight="medium">Post Expense Amount</FormLabel>
                <Controller
                  name="post_expense_amount"
                  control={control}
                  render={({ field }) => (
                    <Input
                      type="number"
                      {...field}
                      value={field.value || ''}
                      onKeyDown={preventEnterSubmitOnInput}
                      onChange={(e) => {
                        const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0
                        field.onChange(value)
                      }}
                      onWheel={preventNumberInputScroll}
                      placeholder="0.00"
                      _placeholder={{ fontSize: 'sm' }}
                      isDisabled={isFormDisabled || !selectedFixedExpense}
                    />
                  )}
                />
                <FormErrorMessage>{errors.post_expense_amount?.message}</FormErrorMessage>
              </FormControl>

              {selectedFixedExpense ? (
                <Box>
                  <Text fontSize="md" fontWeight="medium" mb={1}>
                    Total Expense
                  </Text>
                  <Text fontSize="md" color="gray.500">
                    {(typeof calculatedTotalExpense === 'number' ? calculatedTotalExpense : 0).toFixed(2)}
                  </Text>
                </Box>
              ) : (
                <Box>
                  <Text fontSize="md" fontWeight="medium" mb={1}>
                    Total Expense
                  </Text>
                  <Text fontSize="sm" color="gray.400" fontStyle="italic">
                    Select a fixed expense to view total expense
                  </Text>
                </Box>
              )}
            </Grid>

            {truckTripExpense && (
              <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4}>
                <Box>
                  <Text fontWeight="medium" mb={2}>
                    Truck Trip Expense
                  </Text>
                  <VStack align="flex-start" spacing={1}>
                    <HStack spacing={2}>
                      <Text fontSize="sm">
                        Cash on Hand:
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold">
                        {truckTripExpense.cash_on_hand.toFixed(2)}
                      </Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Text fontSize="sm">
                        Issued Cash:
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold">
                        {truckTripExpense.issued_cash_amount.toFixed(2)}
                      </Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Text fontSize="sm">
                        Remaining Amount:
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold">
                        {truckTripExpense.remaining_amount.toFixed(2)}
                      </Text>
                    </HStack>
                  </VStack>
                </Box>

                <FormControl isInvalid={!!errors.actual_truck_trip_expense_amount}>
                  <FormLabel fontWeight="medium">
                    Actual Truck Trip Expense
                  </FormLabel>
                  <Input
                    type="number"
                    {...register('actual_truck_trip_expense_amount', {
                      setValueAs: (value) =>
                        value === '' || value === null || value === undefined ? undefined : Number(value),
                    })}
                    onKeyDown={preventEnterSubmitOnInput}
                    onWheel={preventNumberInputScroll}
                    placeholder="0.00"
                    _placeholder={{ fontSize: 'sm' }}
                    isDisabled={isFormDisabled || isLoadingTruckTripExpense}
                  />
                  <FormErrorMessage>{errors.actual_truck_trip_expense_amount?.message}</FormErrorMessage>
                </FormControl>
              </Grid>
            )}

            {(!isEditMode || dieselExpenseId) && (
              <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4} mt={2}>
              <FormControl isInvalid={!!errors.diesel_expense_amount}>
                <FormLabel fontWeight="medium">Diesel Expense Amount</FormLabel>
                <Input
                  type="number"
                  {...register('diesel_expense_amount', {
                    setValueAs: (value) =>
                      value === '' || value === null || value === undefined ? undefined : Number(value),
                  })}
                  onKeyDown={preventEnterSubmitOnInput}
                  onWheel={preventNumberInputScroll}
                  placeholder="0.00"
                  _placeholder={{ fontSize: 'sm' }}
                  isDisabled={isFormDisabled}
                />
                <FormErrorMessage>{errors.diesel_expense_amount?.message}</FormErrorMessage>
              </FormControl>

                <FormControl isInvalid={!!errors.purchase_order}>
                  <FormLabel fontWeight="medium">Vishner PO</FormLabel>
                  <Input
                    {...register('purchase_order')}
                    onKeyDown={preventEnterSubmitOnInput}
                    placeholder="Enter Vishner PO"
                    _placeholder={{ fontSize: 'sm' }}
                    isDisabled={isFormDisabled}
                  />
                  <FormErrorMessage>{errors.purchase_order?.message}</FormErrorMessage>
                </FormControl>
              </Grid>
            )}

            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4}>
              <FormControl isInvalid={!!errors.rate}>
                <FormLabel fontWeight="medium">
                  Rate <Text as="span" color="red.500">*</Text>
                </FormLabel>
                {isRateManualEdit ? (
                  <HStack spacing={2}>
                    <Input
                      type="number"
                      {...register('rate', { valueAsNumber: true })}
                      onKeyDown={preventEnterSubmitOnInput}
                      onWheel={preventNumberInputScroll}
                      placeholder="0.00"
                      _placeholder={{ fontSize: 'sm' }}
                      isDisabled={isFormDisabled}
                      flex={1}
                    />
                    {!isEditMode && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (hasRateValueChanged) {
                            // In create mode, if rate was changed, clear the dropdown
                            setValue('rate', undefined as any, { shouldValidate: false })
                            setOriginalRatePerClientValues(null)
                            setHasRateValueChanged(false)
                          } else {
                            // In create mode, restore original values from API
                            if (originalRatePerClientValues) {
                              setValue('rate', originalRatePerClientValues.id, { shouldValidate: false })
                              if (originalRatePerClientValues.tax_percent !== null && originalRatePerClientValues.tax_percent !== undefined) {
                                setValue('tax_percent', originalRatePerClientValues.tax_percent, { shouldValidate: false })
                              }
                              if (originalRatePerClientValues.has_vat !== undefined) {
                                setValue('has_vat', originalRatePerClientValues.has_vat, { shouldValidate: false })
                              }
                              if (originalRatePerClientValues.no_of_days !== undefined) {
                                setValue('no_of_days', originalRatePerClientValues.no_of_days, { shouldValidate: false })
                              }
                              if (originalRatePerClientValues.stack_run !== undefined) {
                                setValue('stack_run', originalRatePerClientValues.stack_run, { shouldValidate: false })
                              }
                              if (originalRatePerClientValues.requirements !== null && originalRatePerClientValues.requirements !== undefined) {
                                setValue('requirements', originalRatePerClientValues.requirements, { shouldValidate: false })
                              }
                              if (originalRatePerClientValues.remarks !== null && originalRatePerClientValues.remarks !== undefined) {
                                setValue('remarks', originalRatePerClientValues.remarks, { shouldValidate: false })
                              }
                            }
                          }
                          setIsRateManualEdit(false)
                        }}
                        isDisabled={isFormDisabled}
                      >
                        Cancel
                      </Button>
                    )}
                    {!isEditMode && <Box height={4} width={4} />}
                  </HStack>
                ) : (
                  <HStack spacing={2}>
                    <Box flex={1}>
                      <Controller
                        name="rate"
                        control={control}
                        render={({ field }) => (
                          <Select
                            value={field.value ?? ''}
                            onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                            placeholder="Select Rate"
                            isDisabled={isEditMode || isLoadingRatePerClients || isFormDisabled || !containerSize}
                            isInvalid={!!errors.rate}
                          >
                            {ratePerClients.map((rate) => (
                              <option key={rate.id} value={rate.id}>
                                {rate.rate}
                              </option>
                            ))}
                          </Select>
                        )}
                      />
                    </Box>
                    <Button
                      size="sm"
                      variant="outline"
                      leftIcon={<Edit size={16} />}
                      onClick={() => {
                        // Get current rate value from selected rate per client
                        const currentRateValue = selectedRatePerClient ? parseFloat(selectedRatePerClient.rate) || 0 : 0
                        setIsRateManualEdit(true)
                        // Set rate input to current rate value instead of clearing
                        setValue('rate', currentRateValue, { shouldValidate: false })
                      }}
                      isDisabled={isEditMode || isFormDisabled || isLoadingRatePerClients || !containerSize}
                    >
                      Edit
                    </Button>
                    <Box height={4} width={4} />
                  </HStack>
                )}
                <FormErrorMessage>{errors.rate?.message}</FormErrorMessage>
              </FormControl>

              {selectedRatePerClient || (isRateManualEdit && rateId) ? (
                <Box>
                  <Text fontSize="md" fontWeight="medium" mb={1}>
                    Total Rate Per Client
                  </Text>
                  <Text fontSize="md" color="gray.500">
                    {(calculatedTotalRatePerClient ?? 0).toFixed(2)}
                  </Text>
                </Box>
              ) : (
                <Box>
                  <Text fontSize="md" fontWeight="medium" mb={1}>
                    Total Rate Per Client
                  </Text>
                  <Text fontSize="sm" color="gray.400" fontStyle="italic">
                    Select a rate to view total rate per client
                  </Text>
                </Box>
              )}
            </Grid>

            {isRateManualEdit && (
              <>
                <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr 1fr' }} gap={4}>
                  <FormControl isInvalid={!!errors.stack_run}>
                    <FormLabel fontWeight="medium">
                      Stack Run <Text as="span" color="red.500">*</Text>
                    </FormLabel>
                    <Input
                      type="number"
                      {...register('stack_run', { valueAsNumber: true })}
                      onKeyDown={preventEnterSubmitOnInput}
                      onWheel={preventNumberInputScroll}
                      placeholder="0.00"
                      _placeholder={{ fontSize: 'sm' }}
                      isDisabled={isFormDisabled}
                    />
                    <FormErrorMessage>{errors.stack_run?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.no_of_days}>
                    <FormLabel fontWeight="medium">
                      Number of Days <Text as="span" color="red.500">*</Text>
                    </FormLabel>
                    <Input
                      type="number"
                      {...register('no_of_days', { valueAsNumber: true })}
                      min={1}
                      onKeyDown={preventEnterSubmitOnInput}
                      onWheel={preventNumberInputScroll}
                      placeholder="1"
                      _placeholder={{ fontSize: 'sm' }}
                      isDisabled={isFormDisabled}
                    />
                    <FormErrorMessage>{errors.no_of_days?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.has_vat}>
                    <FormLabel fontWeight="medium">Has VAT?</FormLabel>
                    <Controller
                      name="has_vat"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          isChecked={field.value ?? true}
                          onChange={(e) => field.onChange(e.target.checked)}
                          isDisabled={isFormDisabled}
                        >
                          Has VAT
                        </Checkbox>
                      )}
                    />
                    <FormErrorMessage>{errors.has_vat?.message}</FormErrorMessage>
                  </FormControl>
                </Grid>

                <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4}>
                  <FormControl isInvalid={!!errors.requirements}>
                    <FormLabel fontWeight="medium">Requirements</FormLabel>
                    <Textarea
                      {...register('requirements')}
                      placeholder="Enter requirements"
                      _placeholder={{ fontSize: 'sm' }}
                      isDisabled={isFormDisabled}
                      rows={3}
                    />
                    <FormErrorMessage>{errors.requirements?.message}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.remarks}>
                    <FormLabel fontWeight="medium">Remarks</FormLabel>
                    <Textarea
                      {...register('remarks')}
                      placeholder="Enter remarks"
                      _placeholder={{ fontSize: 'sm' }}
                      isDisabled={isFormDisabled}
                      rows={3}
                    />
                    <FormErrorMessage>{errors.remarks?.message}</FormErrorMessage>
                  </FormControl>
                </Grid>
              </>
            )}
          </VStack>
        </Box>

        <Box height={1} />
        <Divider borderColor="gray.300" />

        {/* Dates Section */}
        <Box>
          <Text fontSize="md" fontWeight="semibold" color="gray.500" mb={4}>
            Date Information
          </Text>
          <VStack spacing={4} align="stretch">
            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4}>
              <FormControl isInvalid={!!errors.pickup_date}>
                <FormLabel fontWeight="medium">
                  Pickup Date <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <Input
                  type="date"
                  {...register('pickup_date')}
                  onKeyDown={preventEnterSubmitOnInput}
                  placeholder="Select pickup date"
                  _placeholder={{ fontSize: 'sm' }}
                  isDisabled={isFormDisabled}
                />
                <FormErrorMessage>{errors.pickup_date?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.delivered_date}>
                <FormLabel fontWeight="medium">
                  Delivered Date <Text as="span" color="red.500">*</Text>
                </FormLabel>
                <Input
                  type="date"
                  {...register('delivered_date')}
                  onKeyDown={preventEnterSubmitOnInput}
                  placeholder="Select delivered date"
                  _placeholder={{ fontSize: 'sm' }}
                  isDisabled={isFormDisabled}
                />
                <FormErrorMessage>{errors.delivered_date?.message}</FormErrorMessage>
              </FormControl>
            </Grid>
          </VStack>
        </Box>

        <Box height={1} />
        <Divider borderColor="gray.300" />

        {/* Container Information Section */}
        <Box>
          <Text fontSize="md" fontWeight="semibold" color="gray.500" mb={4}>
            Container Information
          </Text>
          <Text as="span" color="red.500" fontSize="sm" fontStyle="italic">* Required</Text>
          <Box height={1} />
          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={4}>
            <Box gridColumn={{ base: '1', lg: '1 / 2' }}>
              <VStack spacing={4} align="stretch">
                 <TableContainer>
                   <Table variant="simple" size="sm" borderWidth="1px" borderColor="gray.300">
                     <Thead bg="gray.100">
                       <Tr>
                         <Th borderWidth="1px" borderColor="gray.300" bg="gray.100">Container</Th>
                         <Th borderWidth="1px" borderColor="gray.300" bg="gray.100" width="80px">Action</Th>
                       </Tr>
                     </Thead>
                    <Tbody>
                      {(() => {
                        const displayContainers = isEditMode ? containers : containerNames.map((name) => ({ id: 0, container_number: name }))
                        if (displayContainers.length === 0) {
                          return (
                            <Tr>
                              <Td colSpan={2} textAlign="center" color="gray.400" fontStyle="italic" borderWidth="1px" borderColor="gray.300">
                                No containers added yet
                              </Td>
                            </Tr>
                          )
                        }
                        return displayContainers.map((container, index) => (
                          <Tr key={isEditMode ? container.id || index : index}>
                            <Td borderWidth="1px" borderColor="gray.300">
                              {container.container_number}
                            </Td>
                            <Td borderWidth="1px" borderColor="gray.300">
                              <Button
                                size="xs"
                                variant="ghost"
                                colorScheme="red"
                                onClick={() => handleDeleteContainer(index)}
                                isDisabled={isFormDisabled || isLoadingContainers}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </Td>
                          </Tr>
                        ))
                      })()}
                    </Tbody>
                   </Table>
                 </TableContainer>

                <HStack spacing={2}>
                  <Input
                    value={containerNameInput}
                    onChange={(e) => setContainerNameInput(e.target.value)}
                    placeholder="Enter container number"
                    _placeholder={{ fontSize: 'sm' }}
                    isDisabled={isFormDisabled || isLoadingContainers}
                    flex={1}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        if (containerNameInput.trim()) {
                          handleAddContainer()
                        }
                      }
                    }}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    leftIcon={<Plus size={16} />}
                    onClick={handleAddContainer}
                    isDisabled={isFormDisabled || isLoadingContainers || !containerNameInput.trim()}
                  >
                    Add Container
                  </Button>
                </HStack>

                <Text fontSize="sm" color="gray.400" fontStyle="italic">
                  * Click +Add Container or press Enter from keyboard to add a container. Adding a container only works if there is a selected container size.
                </Text>
              </VStack>
            </Box>
          </Grid>
        </Box>

        <Box height={1} />

        {/* Submit Button */}
        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={handleCancel} isDisabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText={isEditMode ? 'Saving...' : 'Creating...'}
            isDisabled={isFormDisabled}
          >
            {isEditMode ? 'Save' : 'Create'}
          </Button>
        </HStack>
      </VStack>

      {/* Container Limit Modal */}
      <Modal isOpen={isLimitModalOpen} onClose={onLimitModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Container Limit Exceeded</ModalHeader>
          <ModalBody>
            <Text>{limitModalMessage}</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onLimitModalClose}>
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Container Required Modal */}
      <Modal isOpen={isContainerRequiredModalOpen} onClose={onContainerRequiredModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Container Required</ModalHeader>
          <ModalBody>
            <Text>Container is required to add a waybill. Please add at least one container before proceeding.</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onContainerRequiredModalClose}>
              OK
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Remaining Container Confirmation Modal */}
      <Modal isOpen={isRemainingContainerModalOpen} onClose={handleCancelAddContainer} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Add Container</ModalHeader>
          <ModalBody>
            <Text>You have reached/exceeded your expected containers in this booking. Are you sure to add another one?</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={handleCancelAddContainer}>
              No
            </Button>
            <Button colorScheme="blue" onClick={handleConfirmAddContainer}>
              Yes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}

